import { type Ref, computed } from "vue";
import {
  BIO_GAS_CONTRACT_VERSIONS,
  ContractType,
  FbsCustomerType,
  MeterAssetType,
  type GasContainer,
} from "@/app/fbs-hub-app/shared/@types";

export const useGetFbsCustomerType = (
  gasContainers: Ref<GasContainer[] | undefined>,
) => {
  const firstGasContainer = computed(() => gasContainers.value?.[0]);

  // --- METER CUSTOMERS ---
  // Meter customers can have only one container therefore only the first container has to be checked
  const isMeterCustomer = computed(
    () => firstGasContainer.value?.contract.type === ContractType.MeterContract,
  );

  // A single meter customer has the Z1 asset type of a container
  const isSingleMeterCustomer = computed(() => {
    const hasSingleMeter =
      firstGasContainer.value?.assetType === MeterAssetType.SingleMeterZ1;
    return isMeterCustomer.value && hasSingleMeter;
  });

  // A multi meter customer has the Z2 or Z3 asset type of a container
  const isMultiMeterCustomer = computed(() => {
    const hasMultipleMeters =
      firstGasContainer.value?.assetType === MeterAssetType.MultiMeterZ2 ||
      firstGasContainer.value?.assetType === MeterAssetType.MultiMeterZ3;
    return isMeterCustomer.value && hasMultipleMeters;
  });

  // --- ORDER CUSTOMERS ---
  // Order customers can have multiple containers, but as the contract type is the same for
  // all of them, it is enough to check the first one here
  const isOrderCustomer = computed(
    () => firstGasContainer.value?.contract.type === ContractType.OrderContract,
  );

  // An order customer that has access to bio gas contracts depends on the contract version of the container.
  // This version can differentiate between the containers, therefore all of the containers have to be checked.
  const isBioOrderCustomer = computed(() => {
    if (!gasContainers.value) return false;

    const containersWithBioGasContract = gasContainers.value.filter(
      (container) =>
        BIO_GAS_CONTRACT_VERSIONS.includes(container.contract.version ?? ""),
    );
    return isOrderCustomer.value && containersWithBioGasContract.length > 0;
  });

  const fbsCustomerType = computed(() => {
    if (isSingleMeterCustomer.value) return FbsCustomerType.SingleMeterCustomer;

    if (isMultiMeterCustomer.value) return FbsCustomerType.MultiMeterCustomer;

    if (isBioOrderCustomer.value) return FbsCustomerType.BioOrderCustomer;

    if (isOrderCustomer.value) return FbsCustomerType.NonBioOrderCustomer;

    return undefined;
  });

  return {
    fbsCustomerType,
  };
};
